import type { ProjectLink } from '@lixionary-jira/domain';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, useAction } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import { useCrumbs } from '../../layout/crumbs';
import { Button, LinkButton } from '../../ui/Button';
import { ErrorAlert, PageUnavailable } from '../../ui/feedback';
import { Field, FormSection, Input, Textarea } from '../../ui/forms';
import { ApiError } from '../../api/client';
import { PageHeader, usePageTitle } from '../../ui/misc';
import { ProjectLinksEditor } from './ProjectLinks';

const STEPS = [
  ['Members', 'Add the people who can view and submit work.'],
  ['Context', 'Write Overview, Project structure and Deployment instructions.'],
  ['Repository and builders', 'Register the canonical repository and named builder machines.'],
  ['Environments', 'Name environments and pick the completion target.'],
];

/** PRJ-01: name, unique immutable key, description; then guided setup. */
export function NewProjectPage() {
  usePageTitle('Create project');
  useCrumbs([{ label: 'Projects', to: '/projects' }, { label: 'New project' }]);
  const admin = useIsAdmin();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [links, setLinks] = useState<ProjectLink[]>([]);
  const create = useAction(
    (v: { name: string; key: string; description: string; links: ProjectLink[] }) =>
      api.post<{ key: string }>('/projects', v),
    {
      success: 'Project created',
      invalidate: [['projects']],
      onSuccess: (r) => navigate(`/projects/${r.key}/settings?setup=1`),
    },
  );
  if (!admin) return <PageUnavailable />;
  const suggested = name
    .replace(/[^A-Za-z0-9 ]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 4);
  const effectiveKey = keyTouched ? key : suggested.length >= 2 ? suggested : key;
  const err = create.error instanceof ApiError ? create.error : null;
  return (
    <div className="page narrow">
      <PageHeader
        title="Create project"
        subtitle="The key prefixes every ticket number and cannot change later."
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate({ name, key: effectiveKey, description, links });
        }}
      >
        <FormSection title="Project" description="Shown to members on the project list and dashboard.">
          <Field label="Name" error={err?.fieldError('name')}>
            {(p) => (
              <Input
                {...p}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Atlas app"
                required
                maxLength={120}
              />
            )}
          </Field>
          <Field
            label="Ticket key"
            hint="2–10 uppercase letters or digits, starting with a letter. Tickets will be numbered like APP-42."
            error={err?.fieldError('key')}
          >
            {(p) => (
              <Input
                {...p}
                className="mono"
                value={effectiveKey}
                onChange={(e) => {
                  setKeyTouched(true);
                  setKey(e.target.value.toUpperCase());
                }}
                placeholder="APP"
                required
                maxLength={10}
                style={{ maxWidth: 200 }}
              />
            )}
          </Field>
          <Field label="Description" optional>
            {(p) => (
              <Textarea
                {...p}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            )}
          </Field>
        </FormSection>
        <ProjectLinksEditor links={links} onChange={setLinks} error={create.error} />
        <FormSection title="After creation" description="Settings guides you through the rest of the setup.">
          <ol className="checklist">
            {STEPS.map(([t, d], i) => (
              <li key={t}>
                <span className="mono muted">{i + 1}.</span>
                <span>
                  <span className="strong">{t}</span> — <span className="muted">{d}</span>
                </span>
              </li>
            ))}
          </ol>
        </FormSection>
        <ErrorAlert error={create.error && !err?.details.length ? create.error : null} />
        <div className="form-actions">
          <LinkButton to="/projects">Cancel</LinkButton>
          <Button type="submit" loading={create.isPending}>
            Create project
          </Button>
        </div>
      </form>
    </div>
  );
}
